/* eslint-disable max-lines */
import React, { createRef, useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import axios, { isAxiosError } from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import sanitizeHtml from 'sanitize-html';
import anchorme from 'anchorme';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { Box, Button, TextField } from '@material-ui/core';
import NativeSelect from '@material-ui/core/NativeSelect';
import IconButton from '@material-ui/core/IconButton';
import MomentUtils from '@date-io/moment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import type Event from '@this/domain/appoint/event';
import muiTheme from '@this/components/appoint/mui_theme';
import Dialog from '@material-ui/core/Dialog';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import NotesIcon from '@material-ui/icons/Notes';
import VideocamIcon from '@material-ui/icons/Videocam';
import CancelIcon from '@material-ui/icons/Cancel';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateIcon from '@material-ui/icons/Create';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CloseIcon from '@material-ui/icons/Close';
import AppointContext from '@this/components/appoint/appoint_context';
import Utils from '@this/domain/appoint/utils';
import ParticipantInput from './participant_input';

interface Props {
  event: Event | null;
  x: number;
  y: number;
  onClose?: () => void;
  onCreate?: (event: Event) => void;
  onUpdate?: (event: Event) => void;
  onDelete?: (event: Event) => void;
}

interface DeleteEventResponse {
  message?: string;
}

const onlineMeetingUrlRef = createRef<HTMLDivElement>();
const copyOnlineMeetingUrlToClipBoard = () => {
  Utils.copyElementToClipBoard(onlineMeetingUrlRef.current, false);
};

type EventMeetingType = 'none' | 'google' | 'outlook' | 'zoom';

const getOnlineMeetingName = (meetingType: string) => {
  switch (meetingType) {
    case 'google':
      return 'Google Meet';
    case 'outlook':
      return 'Microsoft Teams';
    case 'zoom':
      return 'Zoom';
    default:
      return '';
  }
};

const getOnlineMeetingUrl = (event: Event) => {
  switch (event.meetingType) {
    case 'google':
      return event.googleMeetUrl;
    case 'outlook':
      return event.outlookOnlineMeetingUrl;
    case 'zoom':
      return event.zoomMeetingJoinUrl;
    default:
      return null;
  }
};

const smallEditFieldStyle: React.CSSProperties = {
  padding: '8px',
  fontSize: '12px',
  background: '#EEE'
};

const EventPopupCard = observer(({ event, x, y, onClose, onUpdate, onDelete }: Props) => {
  const contextValue = useContext(AppointContext);

  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showZoomAuthDialog, setShowZoomAuthDialog] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<string>(event?.start.format('HH:mm') || '09:00');
  const [endTime, setEndTime] = useState<string>(event?.end.format('HH:mm') || '10:00');
  const [copyFlag, setCopyFlag] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleDelete = useCallback(async () => {
    if (!event || isDeleting) {
      return;
    }
    setIsDeleting(true);
    if (onClose) {
      onClose();
    }

    contextValue.setSnackbarContent({ message: '予定を削除しています...', severity: 'info' });
    contextValue.setOpenSnackbar(true);

    const params = event.deleteParams();
    try {
      const res = await axios.delete<DeleteEventResponse>(`/aicalendar/events/${event.id}.json`, { params });
      if (onDelete) {
        onDelete(event);
      }
      if (res.data.message) {
        contextValue.setSnackbarContent({ message: res.data.message, severity: 'success' });
        contextValue.setOpenSnackbar(true);
      }
    } catch (e) {
      const message =
        (isAxiosError(e) && e.response?.data?.message) ||
        'サーバーエラーが発生しました。時間をおいて再度お試しください';
      contextValue.setSnackbarContent({ message, severity: 'error' });
      contextValue.setOpenSnackbar(true);
    } finally {
      setIsDeleting(false);
    }
  }, [event, isDeleting]);

  useEffect(() => {
    if (!event) {
      return;
    }
    // 既存Eventの場合はParticipantsの情報が足りないため同一EmailのCalendarに置き換える
    if (event.id) {
      if (!editingEvent) {
        const participants = _.map(
          event.participants,
          p => _.find(contextValue.calendars, c => p.email === c.email || p.email === c.id) || p
        );
        event.participants = participants;
        event.oldParticipants = participants;
      }
    } else {
      setEditingEvent(event);
      setStartTime(event.start.format('HH:mm'));
      setEndTime(event.end.format('HH:mm'));
    }
  }, [event]);

  const handleCancelEdit = () => {
    setCopyFlag(false);
    setEditingEvent(null);
    if (!event?.id && onClose) {
      onClose();
    }
  };

  const handleTimeChange = (type: 'start' | 'end', time: any) => {
    if (!event || typeof time !== 'string') {
      return;
    }

    const timeArray = time.split(':');
    if (type === 'start') {
      setStartTime(time);
      if (!time.match(/\d{2}:\d{2}/)) {
        return;
      }

      event.start.set({ hour: +timeArray[0], minute: +timeArray[1] });
      if (event.end <= event.start) {
        event.end = event.start.clone().add(1, 'hour');
        setEndTime(event.end.format('HH:mm'));
      }
    } else {
      setEndTime(time);
      if (!time.match(/\d{2}:\d{2}/)) {
        return;
      }

      event.end.set({ hour: +timeArray[0], minute: +timeArray[1] });
      if (event.start >= event.end) {
        event.start = event.end.clone().subtract(1, 'hour');
        setStartTime(event.start.format('HH:mm'));
      }
    }
  };

  const user = contextValue.user;

  const eventTimes: string[] = Array.from(Array(24).keys())
    .map(h => [0, 15, 30, 45].map(m => `${`0${h}`.slice(-2)}:${`0${m}`.slice(-2)}`))
    .flat();

  const handleChangeMeetingType = (meetingType: EventMeetingType) => {
    if (event) {
      if (meetingType !== 'zoom' || user?.appointZoomAccessToken) {
        event.meetingType = meetingType;
      } else {
        setShowZoomAuthDialog(true);
      }
    }
  };

  const renderZoomAuthDialog = () => (
    <ZoomAuthDialog open={showZoomAuthDialog} onClose={() => setShowZoomAuthDialog(false)}>
      <Paper style={{ position: 'relative' }}>
        <IconButton
          size="small"
          onClick={() => setShowZoomAuthDialog(false)}
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <ZoomAuthContent>
          <img
            src="/images/aicalendar/zoom_auth.png"
            height="40"
            alt="Zoomと連携する"
            style={{ display: 'block' }}
          />
          <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.2em' }}>
            Zoomにログインして
            <br />
            連携をしますか？
          </p>
        </ZoomAuthContent>
        <ZoomAuthButtonWrap>
          <Button variant="contained" onClick={() => setShowZoomAuthDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              location.href = '/aicalendar/zoom/authorization/new';
            }}
          >
            連携
          </Button>
        </ZoomAuthButtonWrap>
      </Paper>
    </ZoomAuthDialog>
  );

  const handleSave = async () => {
    if (!editingEvent || isSaving) {
      return;
    }
    setIsSaving(true);
    contextValue.setSnackbarContent({ message: '予定を保存しています...', severity: 'info' });
    contextValue.setOpenSnackbar(true);

    const params = editingEvent.submitParams();
    let url = '';
    try {
      let res;
      if (copyFlag) {
        editingEvent.id = '';
        setCopyFlag(false);
      }
      if (editingEvent.id) {
        // 既存イベント
        url = `/aicalendar/events/${editingEvent.id}.json`;
        res = await axios.put(url, params);
      } else {
        // 新規イベント
        url = `/aicalendar/events.json`;
        res = await axios.post(url, params);
      }
      if (res?.data && res.data.error) {
        contextValue.setSnackbarContent({ message: res.data.error, severity: 'error' });
        contextValue.setOpenSnackbar(true);
      } else {
        if (onClose) {
          onClose();
        }
        setEditingEvent(null);
        if (onUpdate) {
          onUpdate(editingEvent);
        }
        contextValue.setSnackbarContent({ message: '予定を保存しました', severity: 'success' });
        contextValue.setOpenSnackbar(true);
      }
    } catch (e) {
      const message =
        (isAxiosError(e) && e.response?.data?.message) ||
        'サーバーエラーが発生しました。時間をおいて再度お試しください';
      contextValue.setSnackbarContent({ message, severity: 'error' });
      contextValue.setOpenSnackbar(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getCalendarName = (calendar_id: string) => {
    if (contextValue.calendars) {
      const cal = contextValue.calendars.filter(c => c.id === calendar_id);
      if (cal && cal.length > 0) {
        return cal[0].name;
      }
    }
    return calendar_id;
  };

  const getDescriptionHyperLink = (text: string) => {
    const htmlText = anchorme({
      input: text,
      options: {
        attributes: () => {
          const attributes = {
            target: '_blank',
            rel: 'noopener noreferrer'
          };
          return attributes;
        }
      }
    });
    return htmlText;
  };

  const getLinkString = (meetingURL: string) => {
    return <a href={meetingURL}>{meetingURL}</a>;
  };

  const renderTimeSelector = (type: 'start' | 'end') => (
    <>
      {event && (
        <Autocomplete
          options={eventTimes}
          getOptionLabel={option => option}
          filterOptions={(options, _state) => options}
          inputValue={type === 'start' ? startTime : endTime}
          defaultValue={type === 'start' ? startTime : endTime}
          freeSolo
          onInputChange={(_, value) => {
            if (typeof value === 'string' && value !== '') {
              handleTimeChange(type, value);
            }
          }}
          onChange={(_, selected) => {
            if (selected && typeof selected === 'string' && selected.length > 0) {
              handleTimeChange(type, selected);
            }
          }}
          onClose={(e, reason) => {
            if (reason === 'blur') {
              e.stopPropagation();
            }
          }}
          renderInput={params => (
            <TheTextField {...params} variant="outlined" InputLabelProps={{ shrink: true }} />
          )}
          ListboxProps={{ style: { fontSize: 12, marginLeft: -10 } }}
        />
      )}
    </>
  );

  const renderShowEvent = (event: Event) => (
    <DetailWindow style={{ boxShadow: '0px 4px 30px rgba(0,  0,  0,  0.25)' }}>
      <DetailHeader>
        <IconButton
          size="small"
          onClick={() => {
            setCopyFlag(true);
            setEditingEvent(event);
          }}
          style={{ marginLeft: 'auto' }}
        >
          <FilterNoneIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setEditingEvent(event)} style={{ marginLeft: '8px' }}>
          <CreateIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleDelete()}
          disabled={isDeleting}
          style={{ marginLeft: '8px' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onClose && onClose()}
          style={{ marginLeft: '8px', marginRight: '8px' }}
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      </DetailHeader>
      <DetailBody isMobile={contextValue.isMobile}>
        <DetailBox style={{ marginTop: '10px' }}>
          <DetailIcon style={{ marginTop: '5px' }}>
            <img width="15" height="15" src="/images/aicalendar/calendar_icon_02.png" />
          </DetailIcon>
          <DetailText style={{ fontSize: '16px' }}>{event.name}</DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <AccessTimeIcon style={{ fontSize: '20px', marginLeft: '-2px', marginTop: '2px' }} />
          </DetailIcon>
          <DetailText style={{ marginTop: '3px' }}>
            {event.start.format('YYYY年M月D日 HH:mm')} 〜 {event.end.format('YYYY年M月D日 HH:mm')}
          </DetailText>
          {/* <DetailText style={{ top: '20px' }}>{getReminderString(event.reminders || '')}</DetailText> */}
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <RoomOutlinedIcon style={{ fontSize: '25px', marginLeft: '-4px', marginTop: '-2px' }} />
          </DetailIcon>
          <DetailText>
            {event.location && event.location?.indexOf('http') === 0
              ? getLinkString(event.location)
              : event.location}
          </DetailText>
        </DetailBox>
        {event.participants && (
          <DetailBox>
            <DetailIcon>
              <PeopleOutlineIcon style={{ fontSize: '20px', marginLeft: '-2px', marginTop: '-2px' }} />
            </DetailIcon>
            <DetailText>
              {event.participants.length}人のゲスト
              {event.participants.map(p => (
                <Box key={p.id} display="flex" alignItems="center" padding="2px 0">
                  <AccountCircleIcon style={{ marginRight: '4px', color: muiTheme.palette.grey[400] }} />
                  <Box>{p.name}</Box>
                </Box>
              ))}
            </DetailText>
          </DetailBox>
        )}
        <DetailBox>
          <DetailIcon>
            <NotesIcon style={{ fontSize: '20px' }} />
          </DetailIcon>
          <DetailText style={{ top: '-7px', overflowY: 'scroll', maxHeight: '150px', minHeight: '20px' }}>
            {event.description &&
              getDescriptionHyperLink(event.description)
                .split('\n')
                .map((line, i) => <Text key={i} dangerouslySetInnerHTML={{ __html: sanitizeHtml(line) }} />)}
          </DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <VideocamIcon style={{ fontSize: '20px', marginTop: '-1px' }} />
          </DetailIcon>
          <Box>
            <DetailText>Web会議</DetailText>
            {event.meetingType && (
              <DetailMeetingMiniBox>
                <MeetingButton
                  onClick={() => {
                    window.open(getOnlineMeetingUrl(event) || '');
                  }}
                >
                  {getOnlineMeetingName(event.meetingType || '')}に参加する
                </MeetingButton>
                <MeetingLine />
                <DetailMeetingMiniMiniBox>
                  <Box display="flex" alignItems="center">
                    <Box marginRight="10px">URL</Box>
                    <DetailMeetingURLText ref={onlineMeetingUrlRef}>
                      {getOnlineMeetingUrl(event) || ''}
                    </DetailMeetingURLText>
                  </Box>
                  <CopyIconWrap>
                    <CopyIconButton
                      edge="end"
                      color="primary"
                      aria-label="copy"
                      onClick={copyOnlineMeetingUrlToClipBoard}
                    >
                      <FilterNoneIcon fontSize="small" style={{ color: '#fff' }} />
                    </CopyIconButton>
                  </CopyIconWrap>
                </DetailMeetingMiniMiniBox>
              </DetailMeetingMiniBox>
            )}
          </Box>
        </DetailBox>
        <DetailBox style={{ marginTop: '15px' }}>
          <DetailIcon>
            <CalendarTodayIcon style={{ fontSize: '20px', marginTop: '-1px' }} />
          </DetailIcon>
          <DetailText>{getCalendarName(event.calendarId || '')}</DetailText>
        </DetailBox>
        {/* <DetailFooterBox>
              <DetailFooterText>参加しますか？</DetailFooterText>
              <DetailFooterButtonBox>
                <ResponseButton
                  style={{ marginLeft: '80px' }}
                  onClick={() =>
                    handleEventResponse('accepted', event.adjustmentKey || '') && onClose && onClose()
                  }
                >
                  はい
                </ResponseButton>
                <ResponseButton
                  onClick={() =>
                    handleEventResponse('declined', event.adjustmentKey || '') && onClose && onClose()
                  }
                >
                  いいえ
                </ResponseButton>
                <ResponseButton
                  onClick={() =>
                    handleEventResponse('needsAction', event.adjustmentKey || '') && onClose && onClose()
                  }
                >
                  未定
                </ResponseButton>
              </DetailFooterButtonBox>
            </DetailFooterBox> */}
      </DetailBody>
    </DetailWindow>
  );

  const renderEditEvent = (event: Event) => (
    <DetailWindow style={{ boxShadow: '0px 4px 30px rgba(0,  0,  0,  0.25)' }}>
      <DetailHeader>
        <IconButton
          size="small"
          onClick={() => handleCancelEdit()}
          style={{ marginLeft: 'auto', marginRight: '8px' }}
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      </DetailHeader>
      <DetailBody isMobile={contextValue.isMobile}>
        <DetailBox style={{ marginTop: '10px', width: '100%' }}>
          {!contextValue.isMobile && <DetailIcon style={{ marginTop: '5px' }} />}
          <DetailText style={{ fontSize: '16px' }}>
            <TextField
              color="primary"
              value={event.name}
              placeholder="タイトルを追加"
              fullWidth
              onChange={e => {
                event.name = e.target.value;
              }}
              style={{ marginTop: '-2px' }}
            />
          </DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <AccessTimeIcon style={{ fontSize: '20px', marginLeft: '-2px', marginTop: '8px' }} />
          </DetailIcon>
          <DetailText style={{ marginTop: '3px', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Box style={{ padding: '3px 0', display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  autoOk
                  disableToolbar
                  variant="inline"
                  format="YYYY/MM/DD(dd)"
                  margin="none"
                  value={event.start}
                  onChange={date => {
                    if (!date) {
                      return;
                    }
                    event.start.set({ year: date.year(), month: date.month(), date: date.date() });
                  }}
                  inputVariant="outlined"
                  size="small"
                  inputProps={{ style: _.merge({}, smallEditFieldStyle, { width: '90px' }) }}
                  style={{ marginRight: 8, zIndex: 9999 }}
                />
                {renderTimeSelector('start')}
                <Box margin="0 8px">〜</Box>
              </Box>
              <Box style={{ padding: '3px 0', display: 'flex', alignItems: 'center' }}>
                <DatePicker
                  autoOk
                  disableToolbar
                  variant="inline"
                  format="YYYY/MM/DD(dd)"
                  margin="none"
                  value={event.end}
                  onChange={date => {
                    if (!date) {
                      return;
                    }
                    event.end.set({ year: date.year(), month: date.month(), date: date.date() });
                  }}
                  inputVariant="outlined"
                  size="small"
                  inputProps={{ style: _.merge({}, smallEditFieldStyle, { width: '90px' }) }}
                  style={{ marginRight: 8, zIndex: 9999 }}
                />
                {renderTimeSelector('end')}
              </Box>
            </MuiPickersUtilsProvider>
          </DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <RoomOutlinedIcon style={{ fontSize: '25px', marginLeft: '-4px', marginTop: '2px' }} />
          </DetailIcon>
          <DetailText>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={event.location}
              onChange={e => {
                event.location = e.target.value;
              }}
              inputProps={{ style: _.merge({}, smallEditFieldStyle) }}
            />
          </DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <PeopleOutlineIcon style={{ fontSize: '20px', marginLeft: '-2px', marginTop: '2px' }} />
          </DetailIcon>
          <DetailText>
            <ParticipantInput
              participants={event.participants}
              calendars={contextValue.calendars}
              onAdd={calendar => {
                if (event.participants) {
                  event.participants.push(calendar);
                } else {
                  event.participants = [calendar];
                }
              }}
              onRemove={calendar => {
                const participant = _.find(event.participants, p => p.id === calendar.id);
                if (participant && event.participants) {
                  const index = event.participants.indexOf(participant);
                  if (index > -1) {
                    event.participants.splice(index, 1);
                  }
                }
              }}
              inputStyle={_.merge({}, smallEditFieldStyle, { padding: 0 })}
            />
            {/* {event.participants.length}人のゲスト
              {event.participants.map(p => (
                <Box key={p.id} display="flex" alignItems="center" padding="2px 0">
                  <AccountCircleIcon style={{ marginRight: '4px', color: muiTheme.palette.grey[400] }} />
                  <Box>{p.name}</Box>
                </Box>
              ))} */}
          </DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <NotesIcon style={{ fontSize: '20px' }} />
          </DetailIcon>
          <DetailText style={{ top: '-7px' }}>
            <TextField
              variant="outlined"
              multiline
              fullWidth
              value={event.description}
              onChange={e => {
                event.description = e.target.value;
              }}
              style={{ padding: '0', background: '#EEE' }}
              inputProps={{ style: _.merge({}, { padding: '0', height: '60px', fontSize: '12px' }) }}
            />
          </DetailText>
        </DetailBox>
        <DetailBox>
          <DetailIcon>
            <VideocamIcon style={{ fontSize: '20px', marginTop: '-1px' }} />
          </DetailIcon>
          <Box style={{ width: contextValue.isMobile ? '100%' : 'auto' }}>
            <DetailText>Web会議</DetailText>
            <NativeSelect
              value={event.meetingType}
              inputProps={{
                name: 'meeting_type',
                id: 'adjustment-meeting-type',
                style: { width: 'inherit' }
              }}
              onChange={e => handleChangeMeetingType(e.target.value as EventMeetingType)}
              style={{ width: 'inherit', fontSize: contextValue.isMobile ? 14 : 16 }}
            >
              <option value="none">なし</option>
              {contextValue.calendars.some(c => c.provider === 'google') && (
                <option value="google">Google Meet</option>
              )}
              <option value="zoom">Zoom</option>
            </NativeSelect>
          </Box>
          <Box>{renderZoomAuthDialog()}</Box>
        </DetailBox>
        <Box display="flex" alignItems="center" marginTop="20px">
          <TheButton
            variant="outlined"
            color="primary"
            onClick={() => handleCancelEdit()}
            isMobile={contextValue.isMobile}
            style={{ margin: '0 10px 0 auto' }}
          >
            キャンセル
          </TheButton>
          <TheButton
            variant="contained"
            color="primary"
            onClick={() => handleSave()}
            disabled={isSaving}
            isMobile={contextValue.isMobile}
            style={{ marginRight: contextValue.isMobile ? 'auto' : '0' }}
          >
            保存
          </TheButton>
        </Box>
      </DetailBody>
    </DetailWindow>
  );

  return (
    <>
      {event ? (
        <Wrapper isMobile={contextValue.isMobile}>
          <WrapperIn x={x} y={y} elevation={6} isMobile={contextValue.isMobile}>
            {editingEvent ? renderEditEvent(editingEvent) : renderShowEvent(event)}
          </WrapperIn>
        </Wrapper>
      ) : null}
    </>
  );
});

const Wrapper = styled(Paper)<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    display: flex;
    align-content: center;width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    overflow-y: auto;
    align-items: center;
    background: transparent;
  `}
`;

const WrapperIn = styled(Paper)<{ x: number; y: number; isMobile: boolean }>`
  position: absolute;
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  width: 500px;
  z-index: 999;
  background: #fff9ff;
  border-radius: 10px;

  ${props =>
    props.isMobile &&
    `
    position: initial;
    width: 94%;
    margin: 20vh auto;
  `}
`;

const Text = styled(Typography)`
  font-size: 12px;
  display: block;
`;

const TheTextField = styled(TextField)`
  &&& {
    .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
      padding: 0;
    }

    .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
      min-width: 54px;
      padding: 8px;
      background: #eee;
      font-size: 12px;
      background: #eee;
    }

    .MuiAutocomplete-endAdornment {
      display: none;
    }
  }
`;

const DetailWindow = styled.div`
  padding-bottom: 25px;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 500px;
  height: 40px;
  border-radius: 10px 10px 0px 0px;
`;

const DetailBody = styled.div<{ isMobile: boolean }>`
  padding: 0 30px;

  ${props =>
    props.isMobile &&
    `
    padding: 0 15px;
  `}
`;

const DetailBox = styled.div`
  display: flex;
  width: 100%;
  max-width: 500px;
  min-height: 40px;
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.05em;
  color: #606060;
`;

// const DetailMemoBox = styled.div`
//   width: 500px;
//   font-family: Noto Sans JP;
//   font-style: normal;
//   font-weight: 500;
//   font-size: 16px;
//   line-height: 0%;
//   letter-spacing: 0.05em;
//   color: #606060;
// `;

const DetailIcon = styled(Box)`
  min-width: 28px;
  color: #000;
`;

const DetailText = styled(Box)`
  width: 100%;
  max-width: 400px;
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.05em;
  color: #606060;
  margin-bottom: 10px;
`;

// const DetailMeetingBox = styled.div`
//   width: 500px;
//   height: 200px;
//   font-family: Noto Sans JP;
//   font-style: normal;
//   font-weight: 500;
//   font-size: 16px;
//   line-height: 0%;
//   letter-spacing: 0.05em;
//   color: #606060;
// `;

const DetailMeetingMiniBox = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 14px 20px;
  background: #fff9ff;
  border: 1.5px solid #ededed;
  box-sizing: border-box;
  border-radius: 3px;
`;

const DetailMeetingMiniMiniBox = styled.div`
  display: flex;
  align-items: center;
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 0%;
  letter-spacing: 0.05em;
  color: #606060;
`;

const DetailMeetingURLText = styled(Typography)`
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0.05em;
  color: #243565;
  mix-blend-mode: normal;
`;

const MeetingButton = styled.button`
  width: 170px;
  height: 32px;
  background: #243565;
  border: 1px solid #243565;
  box-sizing: border-box;
  border-radius: 3px;
  font-size: 12px;
  color: #ffffff;
  cursor: pointer;
`;

const MeetingLine = styled.hr`
  border: 1px solid #ededed;
  margin: 14px 0;
`;

const TheButton = styled(Button)<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    min-width: 100px;
    font-size: 12px;
  `}
`;

// const ResponseButton = styled(Button)`
//   margin: 3px;
//   width: 48px;
//   height: 30px;
//   background: #ffffff;
//   mix-blend-mode: normal;
//   border: 1px solid #dddddd;
//   box-sizing: border-box;
//   border-radius: 3px;
// `;

// const DetailFooterBox = styled.div`
//   position: relative;
//   left: 0px;
//   top: 20px;
//   padding: 15px 0px 0px 0px;
//   width: 500px;
//   height: 54px;
//   background: #f9f9f9;
//   border-radius: 0px 0px 10px 10px;
// `;

// const DetailFooterText = styled.div`
//   padding: 20px 0px 0px 30px;
//   font-family: Noto Sans JP;
//   font-style: normal;
//   font-weight: bold;
//   font-size: 11px;
//   line-height: 0%;
//   letter-spacing: 0.05em;
//   color: #606060;
//   mix-blend-mode: normal;
// `;

// const DetailFooterButtonBox = styled.div`
//   position: relative;
//   top: -18px;
//   left: 150px;
// `;

const CopyIconWrap = styled(Box)`
  min-width: 30px;
  margin-left: auto;
`;

const CopyIconButton = styled(IconButton)`
  padding: 8px;
  background: ${muiTheme.palette.primary.main};
  box-shadow: 0 0 6px -2px #000;
  border-radius: 50%;
  line-height: 1;
  transform: rotateX(180deg);
`;

const ZoomAuthDialog = styled(Dialog)``;

const ZoomAuthContent = styled(Box)`
  padding: 64px 56px 14px;
`;

const ZoomAuthButtonWrap = styled(Box)`
  display: flex;
  justify-content: center;
  background-color: #f9f9f9;
  padding: 14px 0;

  & button:first-child {
    margin-right: 10px;
  }

  & button {
    width: 105px;
  }
`;

export default EventPopupCard;
